import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, Sparkles, ShieldCheck, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import api from "@/lib/api";
import { useSiteContent } from "@/hooks/useSiteContent";

const HERO_LINES = ["DES IMAGES", "QUI RACONTENT", "VOTRE HISTOIRE"];

const CATEGORIES = [
  { titre: "Portraits Individuels", sous: "Révélez votre personnalité", url: "/images/univers-portrait.jpg", lien: "https://danphotographemariage.gallery.aftershoot.com/collection/photos-en-solo-studio-ext-rieur" },
  { titre: "Couples", sous: "Des liens qui vous sont chers", url: "/images/univers-couple.jpg", lien: "https://danphotographemariage.gallery.aftershoot.com/collection/photo-de-couple" },
  { titre: "Familles", sous: "Des souvenirs pour la vie", url: "/images/univers-famille.jpg", lien: "https://danphotographemariage.gallery.aftershoot.com/collection/photo-famille" },
];

const PILLARS = [
  { icon: Heart, titre: "Une approche humaine", texte: "Je prends le temps de vous écouter et de vous mettre à l'aise, dans une ambiance simple et chaleureuse." },
  { icon: Sparkles, titre: "Une touche créative", texte: "J'aime travailler la composition, la lumière et les détails pour créer des images qui ont une personnalité." },
  { icon: ShieldCheck, titre: "Un service fiable", texte: "Ponctualité, écoute et livraison rapide : je m'investis pleinement dans chaque projet." },
];

export default function Home() {
  const content = useSiteContent();
  const [temoignages, setTemoignages] = useState([]);
  const [tIndex, setTIndex] = useState(0);
  const heroRef = useRef(null);
  const quoteRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "25%"]);
  const { scrollYProgress: quoteProgress } = useScroll({ target: quoteRef, offset: ["start end", "end start"] });
  const quoteY = useTransform(quoteProgress, [0, 1], ["-12%", "12%"]);

  useEffect(() => {
    api.get("/testimonials")
      .then(({ data }) => setTemoignages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (temoignages.length < 2) return;
    const t = setInterval(() => setTIndex((i) => (i + 1) % temoignages.length), 6000);
    return () => clearInterval(t);
  }, [temoignages.length]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[640px] overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <img
            src={content.hero_image || "https://images.unsplash.com/photo-1506863530036-1efeddceb993?auto=format&fit=crop&w=1920&q=85"}
            alt="Photographie signature de Daniel Dulude"
            data-testid="hero-image"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#0A0A0C]" />
        </motion.div>

        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-8"
            data-testid="hero-eyebrow"
          >
            Photographe — Saint-Jean-sur-Richelieu
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] text-[#F8F9FA]" data-testid="hero-title">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.4 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {line === "VOTRE HISTOIRE" ? <span className="gold-text italic">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="text-slate-300 text-sm sm:text-base tracking-[0.2em] uppercase mt-8"
            data-testid="hero-subtitle"
          >
            Portraits • Couples • Familles • Projets spéciaux
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.8 }}
            className="mt-10"
          >
            <Link to="/contact" data-testid="hero-contact-button" className="btn-outline-gold">
              Me contacter <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">Défiler</span>
          <div className="w-[1px] h-10 bg-gradient-to-b from-[#D4AF37] to-transparent" />
        </motion.div>
      </section>

      {/* VIDÉO — LE MONDE ÉQUESTRE */}
      <section
        className="bg-[#0A0A0C] border-y border-[#D4AF37]/20 py-20 lg:py-28"
        data-testid="equestrian-video-section"
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            Le gars de la photo présente
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F8F9FA] mt-5">
            Le monde <span className="gold-text italic">équestre</span>
          </h2>

          <p className="text-slate-300 mt-5 mb-10 max-w-2xl mx-auto">
            La beauté des chevaux, l’émotion et la passion capturées en images.
          </p>

          <div className="relative w-full overflow-hidden rounded-xl border border-[#D4AF37]/30 shadow-2xl"
               style={{ aspectRatio: "16 / 9" }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/PEBktCYvcZc"
              title="Le monde équestre — Le gars de la photo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>

          <p className="text-slate-500 text-sm mt-5">
            Montez le son et laissez-vous emporter par le monde équestre.
          </p>
        </div>
      </section>

      <Marquee />

      {/* 01 — UNIVERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="categories-section">
        <SectionHeading number="01" eyebrow="Les univers" title="Trois façons de raconter votre histoire" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.titre} delay={i * 0.12}>
              <a href={cat.lien} target="_blank" rel="noopener noreferrer" data-testid={`category-card-${cat.titre.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block relative overflow-hidden">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={cat.url} alt={cat.titre} className="img-zoom w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-all duration-500 m-4" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-serif text-2xl sm:text-3xl text-[#F8F9FA]">{cat.titre}</h3>
                  <p className="font-script text-xl text-[#D4AF37] mt-1">{cat.sous}</p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 02 — PHILOSOPHIE */}
      <section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24 lg:py-32" data-testid="philosophy-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionHeading number="02" eyebrow="Ma philosophie" title="Le portrait, à votre image"
                          subtitle="Naturel, authentique et rempli d'émotion." />
          <Reveal delay={0.3}>
            <p className="text-slate-300 text-base sm:text-lg leading-loose mt-10">
              Chaque personne porte une histoire unique. Mon rôle est de la révéler — sans artifice, sans pose forcée.
              Je crée des images naturelles et sincères, où votre personnalité occupe toute la place. Que ce soit en
              studio, à l'extérieur ou dans un lieu qui vous ressemble, je prends le temps qu'il faut pour que vous
              soyez vous-même devant l'objectif.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 03 — POURQUOI DANIEL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="why-daniel-section">
        <SectionHeading number="03" eyebrow="Ce qui me distingue" title="Pourquoi choisir Daniel ?" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {PILLARS.map((p, i) => (
            <Reveal key={p.titre} delay={i * 0.12}>
              <div className="gold-card p-10 text-center h-full" data-testid={`pillar-${p.titre.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-14 h-14 mx-auto border border-[#D4AF37]/40 rotate-45 flex items-center justify-center mb-8">
                  <p.icon size={22} className="text-[#D4AF37] -rotate-45" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl text-[#F8F9FA] uppercase tracking-wide">{p.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-4">{p.texte}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUOTE — PLEINE LARGEUR */}
      <section ref={quoteRef} className="relative h-[70vh] overflow-hidden flex items-center justify-center" data-testid="quote-section">
        <motion.div style={{ y: quoteY }} className="absolute inset-0 scale-125">
          <img
            src={content.quote_image || "https://images.unsplash.com/photo-1604244237695-40ed88b825f2?auto=format&fit=crop&w=1920&q=85"}
            alt="Paysage — lumière et émotion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </motion.div>
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <Reveal>
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#F8F9FA] leading-snug" data-testid="quote-text">
              « Des images qui capturent l'émotion et la beauté du moment. »
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-script text-2xl text-[#D4AF37] mt-6">— Daniel Dulude</p>
          </Reveal>
        </div>
      </section>

      {/* 04 — À PROPOS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="about-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#D4AF37]/25" />
              <img
                src={content.about_image || "https://images.unsplash.com/photo-1587397845856-e6cf49176c70?auto=format&fit=crop&w=1200&q=85"}
                alt="Daniel Dulude avec son appareil photo"
                data-testid="about-photo"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </Reveal>
          <div>
            <SectionHeading center={false} number="04" eyebrow="Depuis plus de 13 ans" title="À propos de Daniel Dulude"
                            subtitle="Une passion pour la photographie et les souvenirs" />
            <Reveal delay={0.2}>
              <p className="text-slate-300 leading-loose mt-8 text-sm sm:text-base">
                Photographe professionnel établi à Saint-Jean-sur-Richelieu, je pratique la photographie depuis plus de
                13 ans. J'aime créer des portraits naturels, authentiques et remplis d'émotion, en studio comme à
                l'extérieur. Chaque séance est pour moi une occasion de raconter une histoire, la vôtre, à travers des
                images sincères et intemporelles. Que ce soit pour un portrait personnel, en famille ou avec votre
                compagnon à quatre pattes, mon objectif est toujours le même : vous offrir des photos qui vous
                ressemblent.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="grid grid-cols-3 gap-4 mt-10" data-testid="about-stats">
                {[["13", "ans d'expérience"], ["100%", "Saint-Jean-sur-Richelieu et environs"], ["∞", "Des souvenirs pour la vie"]].map(([n, label]) => (
                  <div key={label} className="border-l border-[#D4AF37]/40 pl-4">
                    <p className="font-serif text-3xl gold-text">{n}</p>
                    <p className="text-slate-500 text-[11px] uppercase tracking-wider mt-2 leading-snug">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <Link to="/a-propos" data-testid="about-more-button" className="btn-outline-gold mt-10">
                En savoir plus <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

   
           

             

      {/* 06 — AUTOMNE */}
      <section className="relative overflow-hidden" data-testid="autumn-section">
        <div className="absolute inset-0">
          <img
            src={content.autumn_image || "https://images.unsplash.com/photo-1554124819-e6a2f5eed22b?auto=format&fit=crop&w=1920&q=85"}
            alt="Couleurs d'automne"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 60%" }}
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-36 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">06 — Saison en vedette</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6" data-testid="autumn-title">
              L'automne <span className="gold-text italic">arrive !</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-slate-300 text-base sm:text-lg mt-6">C'est le moment parfait pour créer de belles images.</p>
            <p className="font-script text-2xl text-[#F3E5AB] mt-3">Des images authentiques, des souvenirs uniques.</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap justify-center gap-3 mt-10" data-testid="autumn-options">
              {["Studio Quatre Saisons", "À l'extérieur", "Destination de votre choix", "Tous vos projets"].map((o) => (
                <span key={o} className="border border-[#D4AF37]/40 text-[#F3E5AB] text-[11px] uppercase tracking-[0.2em] px-5 py-3 bg-black/40 backdrop-blur-sm">
                  {o}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="font-serif italic text-lg text-slate-300 mt-12 max-w-2xl mx-auto" data-testid="autumn-closing-line">
              L'automne ne dure pas éternellement... profitez-en pour immortaliser un moment qui vous ressemble.
            </p>
            <Link to="/contact" data-testid="autumn-booking-button" className="btn-gold mt-10">
              Réserver ma séance <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
{/* OFFRE — PHOTO IMPRIMÉE */}
<section className="border-y border-[#D4AF37]/10 bg-[#0D0D10] py-20 lg:py-24" data-testid="print-offer-section">
  <div className="max-w-4xl mx-auto px-6 text-center">
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
        Une attention pour vous
      </p>
    </Reveal>

    <Reveal delay={0.1}>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5">
        🎁 Une photo imprimée <span className="gold-text italic">offerte</span>
      </h2>
    </Reveal>

    <Reveal delay={0.2}>
      <p className="text-slate-300 text-base sm:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
        Pour toute séance de <span className="text-[#F3E5AB]">150 $ et plus</span>,
        recevez une photo de votre choix, imprimée et encadrée au format
        <span className="text-[#F3E5AB]"> 11 × 14 po</span>.
      </p>
    </Reveal>

    <Reveal delay={0.3}>
      <p className="font-script text-2xl text-[#D4AF37] mt-6">
        Votre photo • Votre choix • Votre souvenir
      </p>
    </Reveal>
  </div>
</section>
      
      {/* CTA FINAL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="home-contact-cta">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Contactez-moi</h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed">
            Parlons de votre projet. Il me fera plaisir de répondre à vos questions.
          </p>
          <Link to="/contact" data-testid="home-cta-contact-button" className="btn-gold mt-10">
            Me contacter <ArrowRight size={15} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
